import { useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { EBroadcastStatus } from "@api/broadcast";
import { EToySessionState, TLastConsentAction } from "@api/toys";

import { useToysStore } from "@storage/global/useToysStore";
import { useBroadcastStatusDataQuery } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import { useToysCapabilitiesQuery, useToysSessionQuery, useToysSessionTokenQuery } from "@storage/queries/toys";
import { refreshExpiredToysSession } from "@storage/queries/toys/refreshExpiredToysSession";
import { type TToysAvailabilityStorageValue, toysAvailabilityStorage } from "@storage/sessionStorage";

import { STALE_HOURS_ONE } from "@configs/constants";

/**
 * Hook to get the toys session data
 * Pipeline goes through three queries: session token, session, capabilities
 */
export const useToysSession = () => {
	const queryClient = useQueryClient();
	const {
		broadcastStatus,
		id: broadcastId,
		isSuccess: isBroadcastStatusSuccess,
		privateInfo,
	} = useBroadcastStatusDataQuery();
	const {
		activePrivateShowId,
		completeToysSession,
		isToysSessionInitialized,
		setIsOpenControlProposalPopUp,
		setIsOpenToyContainer,
		shouldLoadToysSession,
		triggerToysSession,
	} = useToysStore();

	const isPrivate = broadcastStatus === EBroadcastStatus.PRIVATE;
	const currentPrivateShowId = isPrivate && privateInfo ? `${broadcastId}:${privateInfo.privateRequestId}` : null;
	const isPipelineEnabled =
		activePrivateShowId === currentPrivateShowId &&
		Boolean(currentPrivateShowId) &&
		(shouldLoadToysSession || isToysSessionInitialized);
	const sessionTokenQuery = useToysSessionTokenQuery({
		enabled: isPipelineEnabled,
		privateShowId: activePrivateShowId,
	});
	const token = isPipelineEnabled && sessionTokenQuery.isSuccess ? (sessionTokenQuery.data?.token ?? "") : "";
	const tokenExpiresAt = sessionTokenQuery.data?.expiresAt;
	const tokenExpiryTime = Date.parse(tokenExpiresAt ?? "");
	const sessionToken = tokenExpiryTime <= Date.now() ? "" : token;

	const sessionQuery = useToysSessionQuery(sessionToken, activePrivateShowId);
	const isSessionReady = Boolean(sessionToken) && sessionQuery.isSuccess;
	const sessionState = sessionQuery.data?.state;
	const lastConsentAction = sessionQuery.data?.last_consent?.action;
	const capabilitiesQuery = useToysCapabilitiesQuery(isSessionReady ? sessionToken : "", activePrivateShowId);

	/** Refreshes the toys session token if it is expired with timeout */
	useEffect(() => {
		if (!token || !activePrivateShowId || !tokenExpiresAt) {
			return;
		}

		const expiresAt = Date.parse(tokenExpiresAt);
		if (!Number.isFinite(expiresAt)) {
			return;
		}

		let timeoutId: number;
		const handleExpiry = () => {
			const remainingTime = expiresAt - Date.now();
			if (remainingTime > 0) {
				timeoutId = window.setTimeout(handleExpiry, Math.min(remainingTime, STALE_HOURS_ONE));
				return;
			}

			/** Request errors are already reported by processRequest. */
			void refreshExpiredToysSession(queryClient, activePrivateShowId, token, tokenExpiresAt).catch(() => undefined);
		};

		handleExpiry();

		return () => window.clearTimeout(timeoutId);
	}, [activePrivateShowId, queryClient, token, tokenExpiresAt]);

	/** Checks for toys when a private show starts and restores saved visibility after reload. */
	useEffect(() => {
		if (!isBroadcastStatusSuccess) {
			return;
		}

		if (!currentPrivateShowId) {
			setIsOpenToyContainer(false);
			return;
		}

		triggerToysSession(currentPrivateShowId);

		const toysAvailability = toysAvailabilityStorage.getItemAsJson<TToysAvailabilityStorageValue>();

		if (
			(toysAvailability?.isAvailable || toysAvailability?.isProposalPending) &&
			toysAvailability.privateShowId === currentPrivateShowId
		) {
			setIsOpenToyContainer(toysAvailability.isAvailable);
			return;
		}

		toysAvailabilityStorage.removeItem();
		setIsOpenToyContainer(false);
	}, [currentPrivateShowId, isBroadcastStatusSuccess, setIsOpenToyContainer, triggerToysSession]);

	/** Restores visibility from the current session, including in a new tab or browser. */
	useEffect(() => {
		if (!isSessionReady || sessionQuery.isFetching || !activePrivateShowId) {
			return;
		}

		const toysAvailability = toysAvailabilityStorage.getItemAsJson<TToysAvailabilityStorageValue>();
		const wasAvailable = toysAvailability?.privateShowId === activePrivateShowId && toysAvailability.isAvailable;
		const isProposalPending =
			sessionState === EToySessionState.PENDING && lastConsentAction === TLastConsentAction.OFFER;
		const isAvailable =
			sessionState === EToySessionState.GRANTED_RUNNING ||
			sessionState === EToySessionState.GRANTED_STOPPED ||
			sessionState === EToySessionState.SUSPENDED_OFFLINE ||
			(sessionState === EToySessionState.IDLE && lastConsentAction === TLastConsentAction.REVOKE) ||
			// Keep the stopped-by-user panel visible if control was previously available.
			((sessionState === EToySessionState.IDLE || sessionState === EToySessionState.PENDING) && wasAvailable);

		toysAvailabilityStorage.setItemAsJson<TToysAvailabilityStorageValue>({
			privateShowId: activePrivateShowId,
			isAvailable,
			isProposalPending,
		});

		const { isOpenControlProposalPopUp, isOpenToyContainer } = useToysStore.getState();
		if (isOpenControlProposalPopUp !== isProposalPending) {
			setIsOpenControlProposalPopUp(isProposalPending);
		}
		if (isOpenToyContainer !== isAvailable) {
			setIsOpenToyContainer(isAvailable);
		}
	}, [
		activePrivateShowId,
		isSessionReady,
		lastConsentAction,
		sessionQuery.isFetching,
		sessionState,
		setIsOpenControlProposalPopUp,
		setIsOpenToyContainer,
	]);

	useEffect(() => {
		if (shouldLoadToysSession && isSessionReady && capabilitiesQuery.isSuccess) {
			completeToysSession();
		}
	}, [capabilitiesQuery.isSuccess, completeToysSession, isSessionReady, shouldLoadToysSession]);

	return {
		session: sessionQuery.data,
		capabilities: capabilitiesQuery.data,
		sessionToken,
		isFetching: sessionTokenQuery.isFetching || sessionQuery.isFetching || capabilitiesQuery.isFetching,
		isError: sessionTokenQuery.isError || sessionQuery.isError || capabilitiesQuery.isError,
	};
};
